import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import React, { PropsWithChildren, useMemo } from 'react'
import { ButtonProps } from '../button/button'
import { Link, LinkProps } from '../link/link'
import {
  StyledButton,
  StyledButtonContainer,
  StyledContent,
  StyledCtaInnerWrapper,
  StyledHorizontalHeadingSection,
  StyledImageBackground,
  StyledSubtitle,
  StyledTitle,
  StyledTrapezoid,
  StyledWrapper,
} from './StyledSimpleCta'

type SimpleCtaHeadingSectionProps = {
  children: Array<React.ReactNode>
}

type SimpleCtaTitleProps = {
  //
}

type SimpleCtaSubtitleProps = {
  //
}

type SimpleCtaContentProps = {
  //
}

type SimpleCtaLinkProps = {
  padding?: boolean
} & LinkProps

type SimpleCTAWrapperProps = {
  dimension?: { w: number; h: number }
}


const SimpleCTAHeadingSection: React.FC<PropsWithChildren<SimpleCtaHeadingSectionProps>> = ({ children, ...props }) => {
  return (
    <StyledHorizontalHeadingSection item md={10} sm={12} {...props}>{children}</StyledHorizontalHeadingSection>
  )
}

const SimpleCTATitle: React.FC<PropsWithChildren<SimpleCtaTitleProps>> = (props) => {
  return (
    <StyledTitle {...props}>
      {props.children}
    </StyledTitle>
  )
}

const SimpleCTASubtitle: React.FC<PropsWithChildren<SimpleCtaTitleProps>> = (props) => {
  return (
    <StyledSubtitle {...props}>
      {props.children}
    </StyledSubtitle>
  )
}

const SimpleCTAContent: React.FC<PropsWithChildren<SimpleCtaContentProps>> = (props) => {
  return (
    <StyledContent {...props}>
      {props.children}
    </StyledContent>
  )
}

const ButtonLink = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => (
  <StyledButton {...props} innerRef={ref}>
    {props.children}
  </StyledButton>),
)

const SimpleCTALink: React.FC<PropsWithChildren<SimpleCtaLinkProps>> = ({ padding, href, ...props }) => {
  return (
    <StyledButtonContainer item $extra={padding} md={3} xs={12} sm={4}>
      <Link href={href} {...props} passHref>
        <ButtonLink primary buttonColor="black">{props.children}</ButtonLink>
      </Link>
    </StyledButtonContainer>
  )
}

const SimpleCTAWrapper: React.FC<PropsWithChildren<SimpleCTAWrapperProps>> = ({ dimension, ...props }) => {
  if (!dimension) {
    return null
  }

  return (
    <StyledWrapper {...props} tabIndex={0}>
      <StyledImageBackground>
        <StyledTrapezoid>
          <StyledCtaInnerWrapper>
            {props.children}
          </StyledCtaInnerWrapper>
        </StyledTrapezoid>
      </StyledImageBackground>
    </StyledWrapper>
  )
}

export type SimpleCtaProps = { section?: boolean; title?: boolean; subtitle?: boolean; content?: boolean; cta?: boolean; href?: string } & (
  // Heading container
  | ({ section: true } & SimpleCtaHeadingSectionProps)
  // Title
  | ({ title: true } & SimpleCtaTitleProps)
  // Subtitle
  | ({ subtitle: true } & SimpleCtaSubtitleProps)
  // Content
  | ({ content: true } & SimpleCtaContentProps)
  // Button
  | ({ cta: true; href: string; padding: boolean } & SimpleCtaLinkProps)
  // Wrapper
  | SimpleCTAWrapperProps
)

export const SimpleCta: React.FC<PropsWithChildren<SimpleCtaProps>> = ({ section, title, subtitle, content, cta, href, ...props }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const dimension = useMemo(() => {
    if (isMobile) {
      return { w: 81, h: 103 }
    }

    return { w: 142, h: 180 }
  }, [isMobile])

  if (section) {
    return (
      <SimpleCTAHeadingSection {...props}>
        {props.children}
        <React.Fragment></React.Fragment>
      </SimpleCTAHeadingSection>
    )
  }

  if (title) {
    return (
      <SimpleCTATitle {...props}>
        {props.children}
      </SimpleCTATitle>
    )
  }


  if (subtitle) {
    return (
      <SimpleCTASubtitle {...props}>
        {props.children}
      </SimpleCTASubtitle>
    )
  }

  if (content) {
    return (
      <SimpleCTAContent {...props}>
        {props.children}
      </SimpleCTAContent>
    )
  }


  if (cta) {
    return (
      <SimpleCTALink href={href || '#'} {...props}>
        {props.children}
      </SimpleCTALink>
    )
  }

  return <SimpleCTAWrapper dimension={dimension}>{props.children}</SimpleCTAWrapper>
}